import { createHook, createComponent } from 'reakit-system';
import { ResponsiveValue } from 'styled-system';
import { Theme } from '@mos-cat/ds-theme';
import invariant from 'tiny-invariant';
import { BoxHTMLProps, BoxOptions, BoxProps, useBox } from '../Box/Box';
import { useTheme } from '../hooks/useTheme';
import { StylesOptions, useStyles } from '../hooks/useStyles';

export type ReelOptions = Omit<BoxOptions, 'flexDirection' | 'flexWrap'> & {
  space: ResponsiveValue<keyof Theme['space']>;
  display?: 'flex' | 'inline-flex';
  alignItems?: 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline ';
} & StylesOptions;

export type ReelProps = BoxProps & ReelOptions;

const useReel = createHook<ReelOptions, BoxHTMLProps>({
  name: 'Reel',
  keys: ['space', 'display', 'alignItems'],
  compose: [useTheme, useStyles, useBox],
  useOptions(options) {
    invariant(
      options?.display === undefined ||
        ['flex', 'inline-flex'].includes(options?.display),
      'Значение display отличное от flex и inline-flex'
    );
    invariant(
      options?.alignItems === undefined ||
        ['flex-start', 'flex-end', 'center', 'stretch', 'baseline'].includes(
          options?.alignItems
        ),
      'Значение alignItems отличное от: flex-start, flex-end, center, stretch, baseline'
    );
    return {
      width: 'auto',
      ...options,
      display: options?.display ?? 'flex',
      alignItems: options.alignItems ?? 'flex-start',
      styles: {
        ...options.styles,
        flexWrap: 'nowrap',
        flexDirection: 'row',
        maxWidth:
          options.display === 'inline-flex' && !options.width ? '100%' : 'none',
        '> * + *': {
          marginLeft: options.space,
        },
        '> *': {
          flex: '0 0 auto',
        },
        '> img': {
          height: '100%',
          flexBasis: 'auto',
          width: 'auto',
          maxWidth: 'none',
        },
        overflowX: 'auto',
        overflowY: 'hidden',
        overscrollBehaviorX: 'contain',
      },
    };
  },
});

export const Reel = createComponent({
  useHook: useReel,
  as: 'div',
  memo: true,
});
